// Sentier エントリポイント
// ホーム画面のレンダリングと、各画面への遷移ルーティング
// CLAUDE.md S3-4: 動的データは escapeHtml を通す

import './style.css';
import { showScreen, backToHome, toast, escapeHtml, fmtDuration, fmtDistance, fmtElevation, fmtDate, showConfirm, showAlert } from './ui.js';
import { showMapView, cleanupMapView } from './screens/map-view.js';
import { showMapRecord, cleanupMapRecord } from './screens/map-record.js';
import { getAllTracks, deleteTrack, getTrack, getAllPlans } from './cache.js';
import { exportGpx, downloadBlob } from './gpx.js';
import { showTrackDetail as showTrackDetailScreen, cleanupTrackDetail } from './screens/track-detail.js';

// ============ Service Worker 登録 ============
// 本番ビルド時のみ登録。開発中は既存 SW をすべて解除(古いキャッシュ干渉を防ぐ)
if ('serviceWorker' in navigator) {
  if (import.meta.env && import.meta.env.PROD) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch((e) => {
        console.warn('SW registration failed', e);
      });
    });
  } else {
    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((r) => r.unregister());
    });
  }
}

// ============ テーマ管理 ============
function applyTheme(theme) {
  document.documentElement.setAttribute('data-theme', theme);
  localStorage.setItem('sentier.theme', theme);
  const icon = document.querySelector('#btn-theme svg use');
  if (icon) {
    icon.setAttribute('href', theme === 'dark' ? '#i-sun' : '#i-moon');
  }
}

function initTheme() {
  const saved = localStorage.getItem('sentier.theme');
  if (saved === 'dark' || saved === 'light') {
    applyTheme(saved);
  } else {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    applyTheme(prefersDark ? 'dark' : 'light');
  }
}

function toggleTheme() {
  const current = document.documentElement.getAttribute('data-theme');
  applyTheme(current === 'dark' ? 'light' : 'dark');
}

// ============ ドロワー ============
function openDrawer() {
  const drawer = document.getElementById('drawer');
  drawer.hidden = false;
}

function closeDrawer() {
  document.getElementById('drawer').hidden = true;
}

// ============ ホーム画面の描画 ============
async function renderHome() {
  const plansList = document.getElementById('plans-list');
  const plansEmpty = document.getElementById('plans-empty');
  const tracksList = document.getElementById('tracks-list');
  const tracksEmpty = document.getElementById('tracks-empty');

  // 計画一覧(直近2件)
  const plans = await getAllPlans();
  if (plansList) plansList.innerHTML = '';
  if (plans.length === 0) {
    if (plansEmpty) plansEmpty.hidden = false;
  } else {
    if (plansEmpty) plansEmpty.hidden = true;
    const recentPlans = plans.slice(0, 2);
    for (const p of recentPlans) {
      const div = document.createElement('div');
      div.innerHTML = `
        <button class="card card--button" data-plan-id="${escapeHtml(p.id)}" type="button">
          <p class="card__title">${escapeHtml(p.name || '無題の計画')}</p>
          <p class="card__meta">${escapeHtml(fmtDistance(p.stats?.distance || 0))} · +${Math.round(p.stats?.elevGain || 0)} m · ${escapeHtml(fmtDuration(p.stats?.time || 0))}</p>
        </button>
      `;
      div.querySelector('[data-plan-id]').addEventListener('click', async () => {
        const { showPlanDetail } = await import('./screens/plan-detail.js');
        showPlanDetail(p.id);
      });
      plansList.appendChild(div);
    }
  }

  // 記録一覧(直近3件)
  const tracks = await getAllTracks();
  if (tracksList) tracksList.innerHTML = '';

  if (tracks.length === 0) {
    if (tracksEmpty) tracksEmpty.hidden = false;
    return;
  }

  if (tracksEmpty) tracksEmpty.hidden = true;
  const recent = tracks.slice(0, 3);
  for (const t of recent) {
    const li = document.createElement('li');
    li.innerHTML = `
      <button class="card card--button" data-track-id="${escapeHtml(t.id)}" type="button">
        <p class="card__title">${escapeHtml(t.name || '無題の記録')}</p>
        <p class="card__meta">${escapeHtml(fmtDate(new Date(t.startTs).toISOString()))} · ${escapeHtml(fmtDistance(t.stats?.distance || 0))} · +${Math.round(t.stats?.elevGain || 0)} m · ${escapeHtml(fmtDuration(t.stats?.duration || 0))}</p>
      </button>
    `;
    li.querySelector('[data-track-id]').addEventListener('click', () => {
      showTrackDetail(t.id);
    });
    tracksList.appendChild(li);
  }
}

// 軌跡詳細画面は screens/track-detail.js に集約。再エクスポートして互換維持。
export const showTrackDetail = showTrackDetailScreen;

// ============ アクション処理 ============
function handleAction(action) {
  switch (action) {
    case 'start-record':
      showMapRecord();
      break;
    case 'new-plan':
      (async () => {
        const { showPlanEdit } = await import('./screens/plan-edit.js');
        showPlanEdit(null);
      })();
      break;
    case 'download-tiles':
      showTilesDownload();
      break;
    case 'open-map':
      showMapView({ mode: 'view' });
      break;
    case 'suggest':
      (async () => {
        const { showSuggestScreen } = await import('./screens/suggest.js');
        showSuggestScreen();
      })();
      break;
    case 'search-mountain':
      (async () => {
        const { showMountainSearchScreen } = await import('./screens/mountain-search.js');
        showMountainSearchScreen();
      })();
      break;
    case 'wishlist':
      (async () => {
        const { showWishlistScreen } = await import('./screens/wishlist.js');
        showWishlistScreen();
      })();
      break;
    default:
      console.warn('未知のアクション', action);
  }
}

async function showTilesDownload() {
  const { showTilesScreen } = await import('./screens/tiles.js');
  showTilesScreen();
}

async function handleDrawerScreen(name) {
  closeDrawer();
  switch (name) {
    case 'tracks':
      {
        const { showTracksScreen } = await import('./screens/tracks.js');
        showTracksScreen();
      }
      break;
    case 'plans':
      {
        const { showPlansScreen } = await import('./screens/plans.js');
        showPlansScreen();
      }
      break;
    case 'tiles':
      showTilesDownload();
      break;
    case 'wishlist':
      {
        const { showWishlistScreen } = await import('./screens/wishlist.js');
        showWishlistScreen();
      }
      break;
    case 'share-location':
      {
        const { showShareLocationScreen } = await import('./screens/share-location.js');
        showShareLocationScreen();
      }
      break;
    case 'poi':
      {
        const { showPoiScreen } = await import('./screens/poi.js');
        showPoiScreen();
      }
      break;
    case 'weather':
      {
        const { showWeatherScreen } = await import('./screens/weather.js');
        showWeatherScreen();
      }
      break;
    case 'settings':
      {
        const { showSettingsScreen } = await import('./screens/settings.js');
        showSettingsScreen();
      }
      break;
  }
}

// ============ イベントバインド ============
function bindEvents() {
  document.getElementById('btn-theme')?.addEventListener('click', toggleTheme);
  document.getElementById('btn-menu')?.addEventListener('click', openDrawer);
  document.getElementById('btn-drawer-close')?.addEventListener('click', closeDrawer);
  document.getElementById('drawer-backdrop')?.addEventListener('click', closeDrawer);
  document.getElementById('btn-settings')?.addEventListener('click', () => handleDrawerScreen('settings'));

  document.querySelectorAll('#screen-home .action[data-action]').forEach((btn) => {
    btn.addEventListener('click', () => handleAction(btn.dataset.action));
  });

  document.querySelectorAll('#drawer .action[data-screen]').forEach((btn) => {
    btn.addEventListener('click', () => handleDrawerScreen(btn.dataset.screen));
  });

  // 動的画面の戻るボタン(イベント委譲)
  // map-record は記録ロスト防止のため別途バインドして伝播停止しているのでここには来ない
  document.getElementById('screen-mount').addEventListener('click', (e) => {
    const back = e.target.closest('[data-action="back"]');
    if (back) {
      cleanupMapView();
      cleanupTrackDetail();
      backToHome();
      renderHome();
    }
  });

  // ブラウザ戻る対応
  window.addEventListener('popstate', () => {
    const mountContent = document.getElementById('screen-mount').innerHTML;
    if (mountContent.trim()) {
      cleanupMapView();
      cleanupTrackDetail();
      backToHome();
      renderHome();
    }
  });
}

// ============ 左端スワイプで戻る (iOS 風) ============
function bindSwipeBack() {
  let touchStartX = 0;
  let touchStartY = 0;
  let active = false;

  document.addEventListener('touchstart', (e) => {
    if (e.touches.length !== 1) return;
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
    active = touchStartX < 24;
  });

  document.addEventListener('touchmove', (e) => {
    if (!active || e.touches.length !== 1) return;
    const dx = e.touches[0].clientX - touchStartX;
    const dy = e.touches[0].clientY - touchStartY;
    if (dx > 80 && dx > Math.abs(dy) * 1.5) {
      active = false;
      // 記録中はスワイプで戻らない(誤操作防止)
      const recScreen = document.getElementById('screen-map-record');
      if (recScreen) return;
      const mountContent = document.getElementById('screen-mount').innerHTML;
      if (mountContent.trim()) {
        cleanupMapView();
        cleanupTrackDetail();
        backToHome();
        renderHome();
      }
    }
  });

  document.addEventListener('touchend', () => {
    active = false;
  });
}

// ============ 位置情報の許可フロー ============
// iOS Safari は「ユーザー操作の直後」でしか許可ダイアログを出さないため、
// 初回起動時にホーム画面冒頭に「位置情報を許可する」ボタンを表示する。
async function ensureGeolocationPrompt() {
  if (!('geolocation' in navigator)) return;

  // 既に許可済み(localStorage に過去の位置情報あり)なら何もしない
  const saved = localStorage.getItem('sentier.lastPosition');
  if (saved) return;

  // Permissions API で状態確認(iOS Safari は未対応の場合あり)
  let state = 'prompt';
  if (navigator.permissions && navigator.permissions.query) {
    try {
      const res = await navigator.permissions.query({ name: 'geolocation' });
      state = res.state;
    } catch (e) {
      /* iOS では失敗するケースあり、無視 */
    }
  }
  if (state === 'granted') return;

  // プロンプトカードを表示
  const home = document.getElementById('screen-home');
  if (!home) return;
  if (document.getElementById('geo-permission-prompt')) return;

  const prompt = document.createElement('section');
  prompt.className = 'section';
  prompt.id = 'geo-permission-prompt';
  prompt.innerHTML = `
    <div class="card" style="border-color: var(--ink); border-width: 1px;">
      <p class="card__title" style="font-family: var(--font-serif);">位置情報を有効化</p>
      <p class="card__body">
        Sentier は GPS で現在地を取得して登山地図に表示します。
        位置情報は端末内のみに保存され、外部に送信されることはありません(明示的な共有操作時を除く)。
      </p>
      <button class="btn btn--ink btn--block" id="btn-geo-grant" type="button" style="margin-top: 12px;">
        <svg class="icon"><use href="#i-target"/></svg>
        <span>位置情報を許可する</span>
      </button>
      <p style="font-size: 11px; color: var(--mute); margin-top: 8px; text-align: center;">
        ボタンを押すと iOS の許可ダイアログが表示されます
      </p>
    </div>
  `;

  // hero の直後に挿入
  const hero = home.querySelector('.hero');
  if (hero) hero.after(prompt);
  else home.prepend(prompt);

  document.getElementById('btn-geo-grant')?.addEventListener('click', () => {
    const btn = document.getElementById('btn-geo-grant');
    btn.disabled = true;
    btn.innerHTML = '<span>取得中…</span>';

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        try {
          localStorage.setItem(
            'sentier.lastPosition',
            JSON.stringify({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              ts: Date.now(),
            })
          );
        } catch (e) { /* noop */ }
        prompt.remove();
        toast('位置情報を有効化しました');
      },
      (err) => {
        btn.disabled = false;
        btn.innerHTML = '<svg class="icon"><use href="#i-target"/></svg><span>位置情報を許可する</span>';
        if (err.code === 1) {
          showAlert({
            title: '位置情報が拒否されています',
            body: 'iPhone の設定アプリ → プライバシーとセキュリティ → 位置情報サービス → Safari Web サイト で「このAppの使用中は許可」に設定してください。',
          });
        } else if (err.code === 2) {
          showAlert({
            title: '位置情報を取得できませんでした',
            body: '電波が弱い・屋内などの可能性があります。屋外で再度お試しください。',
          });
        } else if (err.code === 3) {
          showAlert({
            title: 'タイムアウト',
            body: '位置情報の取得がタイムアウトしました。再度お試しください。',
          });
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  });
}

// ============ 初期化 ============
function init() {
  initTheme();
  bindEvents();
  bindSwipeBack();
  renderHome();
  ensureGeolocationPrompt();
}

document.addEventListener('DOMContentLoaded', init);
